import { computed, reactive, ref } from 'vue'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
// Must run before getAuth() below: initializes the default Firebase app.
// Importing this here (rather than relying on callers to import it first)
// guarantees init order regardless of which module pulls in appConfig.js first.
import '@/firebase'
import { logAction } from '@/utils/logAction'
const { hostname } = window.location


// Pick the backend API endpoint from the current frontend host.
// Production uses the public API; every non-localhost, non-production host uses staging.
export const API_BASE_URL = (() => {
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3000/api'
  }
  if (hostname === 'quotastic.ca') {
    return 'https://api.quotastic.ca/api'
  }
  return 'https://api-staging.quotastic.ca/api'
})()



//───────────────────────────────────────────────────────────
// Firebase
//───────────────────────────────────────────────────────────
// Global Firebase auth snapshot used by stores/views that only need the current user identity.
export const authState = reactive({
  uid: null,
  email: null
})

// Auth lifecycle flags. `authResolved` lets views wait until Firebase has emitted
// the first auth state before deciding what to render.
export const isAdmin = ref(false)
export const authResolved = ref(false)

// Returns the current Firebase ID token for authenticated backend requests.
export async function getAuthToken() {
  return getAuth().currentUser?.getIdToken() ?? null
}

//───────────────────────────────────────────────────────────
// End Firebase
//───────────────────────────────────────────────────────────


// Keep the company shape stable so components can bind to fields before data loads.
function createEmptyCompany() {
  return {
    id: null,
    company_id: '',
    owner_firebase_uid: '',
    company_name: '',
    industry: null,
    sub_industry: null,
    company_email: '',
    company_phone: '',
    main_contact_name: '',
    google_review_url: '',
    facebook_review_url: '',
    google_rating: null,
    google_review_count: null,
  }
}

export const company = reactive(createEmptyCompany())

// Loads the company owned by the signed-in user, then hydrates related website data.
async function getCompanyID() {
  if (!authState.uid) {
    Object.assign(company, createEmptyCompany())
    return
  }

  try {
    const token = await getAuthToken()
    const response = await fetch(
      `${API_BASE_URL}/companies?owner_firebase_uid=${authState.uid}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to load company')
    }

    const companyRecord = data.company || data.companies?.[0] || null

    Object.assign(company, createEmptyCompany(), companyRecord || {})

    if (company.company_id) {
      retrieveWebsite(company.company_id)
    }
  } catch (error) {
    console.error('getCompanyID error:', error)
    Object.assign(company, createEmptyCompany())
  }
}

// Admin status is verified by the backend instead of trusting client-side state.
async function checkAdmin(user) {
  try {
    const token = await user.getIdToken()
    const response = await fetch(`${API_BASE_URL}/admin/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await response.json()
    isAdmin.value = response.ok && data.isAdmin === true
  } catch (err) {
    isAdmin.value = false
    console.error('[checkAdmin] fetch failed:', err)
  }
}

const auth = getAuth()

// Central Firebase auth listener. This is the one place that updates auth,
// admin, company, and login-audit state after sign-in/sign-out.
onAuthStateChanged(auth, async (user) => {
  authState.uid = user?.uid || null
  authState.email = user?.email || null

  if (!user) {
    isAdmin.value = false
    Object.assign(company, createEmptyCompany())
    authResolved.value = true
    return
  }

  await checkAdmin(user)

  if (!isAdmin.value) {
    getCompanyID()
    user.getIdToken().then(token => logAction(token, { action: 'login', label: 'Signed in' })).catch(() => {})
  }

  authResolved.value = true
})

// Navigation state
export const selectedSetting = ref('/settings/company')
export const selectedAdmin = ref('/admin/users')

// Site data
export const website = reactive({})

// Loads the first website record for a company into the shared reactive object.
export async function retrieveWebsite(company) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/websites_draft?company_id=${company}`
    )
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error)
    }

    Object.assign(website, data.websites?.[0] || {})
  } catch (error) {
    console.error('loadWebsite error:', error)
    throw error
  }
}


















//───────────────────────────────────────────────────────────
// Dev Users
//───────────────────────────────────────────────────────────
// Firebase UIDs with access to developer-only UI paths.
const DEV_UIDS = new Set([
  'w1eG7Gu0bEfalXS1gwI9IaCKQJ52',
 //. 'Mkd3XFBu0dQ6lHHBae1tMme21GC3'
])

 

export const isDevUser = computed(() =>
  Boolean(authState.uid && DEV_UIDS.has(authState.uid))
)

//───────────────────────────────────────────────────────────
// End  Dev Users
//───────────────────────────────────────────────────────────
